import { useEffect, useRef, useState } from "react";
import { BadgeCheck, ChevronLeft, MoreVertical, RotateCcw, SendHorizontal, X } from "lucide-react";
import { WhatsAppMark } from "../../mocks/BrandMarks";
import ProductLayout from "./sai/ProductLayout";
import { IDS } from "./whatsappIds";
import "./WhatsApp.css";

// WhatsApp, ported from sai's WhatsAppScreen (brochure-demo @ 2a7c405) into our
// shell: ProductLayout renders our <Screen> via SaiProductFrame; IDs are
// data-trapezium-* via toTrapezium; "Next product" follows our STEPS. His
// visitor session is not read: the business account is "Your Lab".
//
// His notes: threads that play out on a phone. Since the 2026-10-20 meeting
// there are three steps: delivery (the report arriving as a picture of the
// patient's own results; it absorbed "one way", which showed the same thing),
// a two-way thread that answers but never diagnoses, and one recall (the
// report's own recheck date, or a cohort from Analytics; it absorbed "book
// recall"). Before the first thread a short intro plays in the phone: the
// WhatsApp mark flashes in, then three key figures (illustrative), then the
// walkthrough continues. Replay plays the intro and the thread again; under
// reduced motion there is no intro. Content: the founder's 25-whatsapp.html.
// The wording is illustrative: every one-way message is a template Meta
// approves first.

const SCREEN = "whatsapp"; // his screen key (our product.whatsapp)
const ORG = "Your Lab"; // his readSession() fallback; no visitor session here

const REDUCE = "(prefers-reduced-motion: reduce)";
const prefersStill = () => typeof window.matchMedia === "function" && window.matchMedia(REDUCE).matches;

// The intro: how long it stays before the thread starts, and its figures
// (illustrative, not measured).
const INTRO_MS = 2400;
const INTRO_STATS = [
  ["9 in 10", "reports opened"],
  ["< 5 min", "to first open"],
  ["2×", "more rechecks booked"],
];

function Ring({ v, label, sub }) {
  const c = 150.8;
  return (
    <span className="wa-info">
      <svg viewBox="0 0 200 92" aria-hidden="true">
        <rect width="200" height="92" fill="#F7FAFF" />
        <circle cx="40" cy="46" r="24" fill="none" stroke="#E4EAF3" strokeWidth="7" />
        <circle cx="40" cy="46" r="24" fill="none" stroke="#3567D6" strokeWidth="7" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - v / 100)} transform="rotate(-90 40 46)" />
        <text x="40" y="52" textAnchor="middle" fontSize="18" fontWeight="700" fill="#10192B">{v}</text>
        <text x="76" y="36" fontSize="11" fontWeight="600" fill="#10192B">{label}</text>
        <text x="76" y="52" fontSize="9" fill="#5A6B87">{sub}</text>
        <g fontSize="8">
          <rect x="76" y="60" width="36" height="15" rx="7" fill="#FDECEA" /><circle cx="84" cy="67.5" r="2.4" fill="#D93A2E" /><text x="90" y="71" fill="#B3291C">Lipids</text>
          <rect x="116" y="60" width="34" height="15" rx="7" fill="#FEF5E7" /><circle cx="124" cy="67.5" r="2.4" fill="#C9741A" /><text x="130" y="71" fill="#9A6413">Sugar</text>
          <rect x="154" y="60" width="34" height="15" rx="7" fill="#E9F9F2" /><circle cx="162" cy="67.5" r="2.4" fill="#0F9B72" /><text x="168" y="71" fill="#0F7A5A">Liver</text>
        </g>
      </svg>
      <span className="wa-cap">Generated for you. Not a template.</span>
    </span>
  );
}

function Trend({ name, vals, unit, note }) {
  const W = 200; const H = 86; const P = { l: 8, r: 8, t: 22, b: 16 };
  const mn = Math.min(...vals) * 0.94; const mx = Math.max(...vals) * 1.06;
  const X = (i) => P.l + (i * (W - P.l - P.r)) / (vals.length - 1);
  const Y = (v) => P.t + (1 - (v - mn) / (mx - mn)) * (H - P.t - P.b);
  return (
    <span className="wa-info">
      <svg viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
        <rect width={W} height={H} fill="#F7FAFF" />
        <text x="8" y="14" fontSize="10" fontWeight="600" fill="#10192B">{name}</text>
        <text x={W - 8} y="14" textAnchor="end" fontSize="10" fontWeight="700" fill="#C0332A">{vals[vals.length - 1]} {unit}</text>
        <polyline points={vals.map((v, i) => `${X(i)},${Y(v)}`).join(" ")} fill="none" stroke="#C0332A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {vals.map((v, i) => <circle key={v} cx={X(i)} cy={Y(v)} r={i === vals.length - 1 ? 3.4 : 2.4} fill={i === vals.length - 1 ? "#C0332A" : "#fff"} stroke="#C0332A" strokeWidth="1.6" />)}
        <text x="8" y={H - 4} fontSize="7.5" fill="#93A2BC">{note}</text>
      </svg>
      <span className="wa-cap">Your own last four visits.</span>
    </span>
  );
}

const ring = <Ring v={72} label="Your report is ready" sub="Four to look at, eight are fine" />;
const trend = <Trend name="LDL cholesterol" vals={[118, 129, 141, 162]} unit="mg/dL" note="Sep 24 → Sep 26" />;

// d = day stamp; k = in | out | typing; body; at = time; btn = a button line
const THREADS = {
  ready: [{ d: "18 September" },
    { k: "in", art: ring, body: <>Namaste Ramesh 🙏 Everything from <b>18 September</b>, in one picture. Four things need a look. Nothing urgent.</>, btn: "Open the full report", at: "9:04 am" },
    { d: "2 March" },
    { k: "in", body: <>Your <b>Full Body Advanced</b> package renews this month. Home collection is free on renewal.</>, btn: "Renew now", at: "11:30 am" }],
  twoway: [{ d: "Today" },
    { k: "out", body: "My LDL is 162. Is that dangerous?", at: "7:41 pm" }, { k: "typing" },
    { k: "in", body: "162 mg/dL is above the normal range, which is under 100. It is not an emergency, but it is worth acting on. It usually comes down with diet and activity over about three months.", at: "7:41 pm" },
    { k: "in", body: <>Your report already lists what tends to help, and suggests a recheck in 12 weeks. <b>I cannot tell you what is wrong with you.</b> For that you need a doctor.</>, btn: "Talk to a doctor", at: "7:41 pm" },
    { k: "out", body: "Should I stop my BP tablet then?", at: "7:43 pm" }, { k: "typing" },
    { k: "in", body: "That is not something I can answer, and I would not want to guess with your medicines. Please do not change anything without asking the doctor who prescribed it.", btn: "Connect me to a doctor", at: "7:43 pm" }],
  recall: [{ d: "11 December" },
    { k: "in", art: trend, body: <>Namaste Ramesh 🙏 Your report in September asked for a <b>lipid recheck after 12 weeks</b>. That is this week, and your last three results have been climbing.</>, btn: "Book a slot", at: "10:12 am" },
    { k: "out", body: "Sunday morning possible?", at: "10:19 am" }, { k: "typing" },
    { k: "in", body: "Yes, Sunday 15th, 7:30 to 11:00 am, home collection. The same lipid panel, so the numbers line up on the same graph.", btn: "Confirm Sunday 7:30 am", at: "10:19 am" },
    { k: "out", body: "Confirmed 👍", at: "10:20 am" },
    { k: "in", body: "Booked. Our technician will call you Saturday evening to confirm the address.", at: "10:20 am" }],
};

const STEPS = [
  { key: "delivery", nav: "Delivery", thread: "ready", extra: "vs", head: "It arrives where they actually read things.",
    lede: "From your verified account, as a picture of their own results.",
    points: [["A verified business account", "Your green tick and your name. No app, no login"], ["A picture, not a notice", "Built from their own values"], ["Analytics powered by NirogGyan's intelligent analytics", "Who opened, who read, who booked"]] },
  { key: "two-way", nav: "Two-way", thread: "twoway", extra: "never", head: "It answers, without diagnosing.",
    lede: "It explains, and hands over to a doctor when it should.",
    points: [["It explains, in their language", "Same knowledge bank as the report"], ["A human is always one tap away", "The hard questions offer it first"], ["Inside the 24-hour window", "Free replies only after the patient writes first"]] },
  { key: "report-recall", nav: "Recall", thread: "recall", head: "Every report schedules its follow-up.",
    lede: "The recheck date in the report, or a cohort from Analytics, becomes a message. They book in the thread.",
    points: [["The date came from the report", "Set when the result was signed off"], ["Or a cohort from your whole book", "The clicks that built the list also send it"], ["Booked in the thread", "No call centre, no form, no app"]] },
];

function Intro() {
  return (
    <div className="wa-intro">
      <WhatsAppMark size={76} className="wa-intro-mark" />
      <ul className="wa-intro-stats">
        {INTRO_STATS.map(([v, l], i) => (
          <li key={l} style={{ "--i": i }}><b>{v}</b><span>{l}</span></li>
        ))}
      </ul>
      <span className="wa-intro-note">Illustrative figures</span>
    </div>
  );
}

function Msg({ m }) {
  if (m.d) return <p className="wa-day">{m.d}</p>;
  if (m.k === "typing") return <p className="wa-typing" aria-label="Typing"><i /><i /><i /></p>;
  return (
    <div className="wa-msg" data-k={m.k}>
      {m.art}
      <span>{m.body}</span>
      <span className="wa-tm">{m.at}{m.k === "in" ? <i>✓✓</i> : null}</span>
      {m.btn ? <span className="wa-btn">{m.btn}</span> : null}
    </div>
  );
}

export default function WhatsApp() {
  const org = ORG;
  const [active, setActive] = useState(0);
  const [shown, setShown] = useState([]);
  const [run, setRun] = useState(0); // bump to replay
  const [intro, setIntro] = useState(() => !prefersStill());
  const threadRef = useRef(null);
  const step = STEPS[active];

  // The intro holds the phone for INTRO_MS, then hands over to the thread.
  useEffect(() => {
    if (!intro) return undefined;
    const timer = setTimeout(() => setIntro(false), INTRO_MS);
    return () => clearTimeout(timer);
  }, [intro, run]);

  // Play the thread: day stamps and messages one after another; a typing
  // indicator stands in for a pause before a reply and is then removed.
  useEffect(() => {
    const msgs = THREADS[step.thread];
    // Timers are cleared on unmount and on every replay; under reduced motion
    // the whole thread shows at once, with no typing indicator.
    if (prefersStill()) { setShown(msgs.filter((m) => m.k !== "typing")); return undefined; }
    setShown([]);
    if (intro) return undefined;
    const timers = [];
    let t = 150;
    msgs.forEach((m) => {
      if (m.k === "typing") {
        timers.push(setTimeout(() => setShown((s) => [...s, m]), t));
        t += 850;
        timers.push(setTimeout(() => setShown((s) => s.filter((x) => x !== m)), t));
        return;
      }
      timers.push(setTimeout(() => setShown((s) => [...s, m]), t));
      t += m.d ? 260 : 650;
    });
    return () => timers.forEach(clearTimeout);
  }, [step.thread, run, intro]);

  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [shown]);

  const done = shown.filter((m) => m.k !== "typing").length === THREADS[step.thread].filter((m) => m.k !== "typing").length;

  const replay = () => {
    setIntro(!prefersStill());
    setRun(run + 1);
  };

  // Choosing a step cuts the intro short.
  const onStep = (i) => {
    setIntro(false);
    setActive(i);
  };

  const visual = (
    <div className="wa-wrap" data-trapezium-view={intro ? "intro" : `${step.thread} ${done ? "played" : "playing"}`}>
      <div className="wa-phone">
        <div className="wa">
          <div className="wa-top">
            <ChevronLeft size={18} aria-hidden="true" />
            <span className="wa-av">{org.slice(0, 1).toUpperCase()}</span>
            <span className="wa-who"><b>{org}<BadgeCheck size={13} aria-hidden="true" /></b><span>Business account</span></span>
            <MoreVertical size={16} aria-hidden="true" />
          </div>
          <p className="wa-biz">Messages are end-to-end encrypted. This business uses a secure service to manage this chat.</p>
          <div className="wa-thread" ref={threadRef} aria-live="polite">
            {shown.map((m, i) => <Msg key={`${step.thread}-${i}-${m.k || "d"}`} m={m} />)}
          </div>
          <div className="wa-bar"><span>Message</span><i><SendHorizontal size={14} aria-hidden="true" /></i></div>
          {intro ? <Intro key={run} /> : null}
        </div>
      </div>
      <button type="button" className="wa-replay" onClick={replay} aria-label="Replay the WhatsApp chat" data-trapezium-id={IDS.replay}>
        <RotateCcw size={14} aria-hidden="true" />Replay
      </button>
    </div>
  );

  const extra = step.extra === "vs" ? (
    <div className="wa-vs">
      <p>What everyone else sends</p>
      <span className="wa-old">Dear Patient, your lab report is ready. Please click the link below to download. Thank you for choosing us.</span>
      <span className="wa-vs-note">A notification with nothing in it. It tells the patient nothing they did not already know.</span>
    </div>
  ) : step.extra === "never" ? (
    <div className="wa-never">
      <p>What it will never do</p>
      <ul>
        {["Diagnose, or name a condition the report does not", "Tell anyone to start, stop or change a medicine", "Give a prognosis, or say how serious something is", "Answer outside what is in that patient's own report"].map((x) => (
          <li key={x}><X size={13} strokeWidth={3} aria-hidden="true" />{x}</li>
        ))}
      </ul>
    </div>
  ) : null;

  return (
    <ProductLayout
      screenKey={SCREEN}
      steps={STEPS}
      active={active}
      onStep={onStep}
      state={`step-${STEPS[active].key}`}
      visual={visual}
      controls={extra}
      note="Message wording is illustrative. Every one-way message is a WhatsApp template that Meta approves before it can be sent."
    />
  );
}
